import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Modal, Select, Spin, Table, Tag } from "antd";
import { toast } from "react-toastify";
import SidebarNav from "../sidebar";
import {
  getCoin,
  getFireblocksEnv,
  getFireblocksSupportedAssets,
  postFireblocksSyncMapping,
} from "../../../service/api";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import ReactSelect from "react-select";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

const ENV_COLOR = { sandbox: "#faad14", production: "#ff4d4f" };

const ALL_COLUMNS = [
  {
    key: "assetId",
    title: "Asset ID",
    defaultVisible: true,
    render: (r) => <b>{r?.assetId || "-"}</b>,
  },
  {
    key: "name",
    title: "Name",
    defaultVisible: true,
    render: (r) => r?.name || "-",
  },
  {
    key: "nativeAsset",
    title: "Native",
    defaultVisible: true,
    render: (r) => r?.nativeAsset || "-",
  },
  {
    key: "decimals",
    title: "Decimals",
    defaultVisible: false,
    render: (r) =>
      r?.decimals !== undefined && r?.decimals !== null ? r.decimals : "-",
  },
  {
    key: "contractAddress",
    title: "Contract",
    defaultVisible: false,
    render: (r) =>
      r?.contractAddress ? (
        <span style={{ fontSize: 11, wordBreak: "break-all" }}>
          {r.contractAddress}
        </span>
      ) : (
        <span style={{ opacity: 0.5 }}>—</span>
      ),
  },
  {
    key: "blockchain",
    title: "Blockchain",
    defaultVisible: true,
    render: (r) => r?.blockchain || "-",
  },
  {
    key: "_state",
    title: "Mapping",
    defaultVisible: true,
    render: (row) => {
      if (row._state === "mapped")
        return (
          <Tag color="green">
            Mapped → {row.coinSymbol}/{row.networkKey}
          </Tag>
        );
      if (row._state === "drift")
        return (
          <Tag color="orange">
            Drift — {row.coinSymbol}/{row.networkKey}
          </Tag>
        );
      return <Tag color="default">Unmapped</Tag>;
    },
  },
];

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Unmapped", value: "unmapped" },
  { label: "Already mapped", value: "mapped" },
  { label: "Drift detected", value: "drift" },
];

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "200px",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

/**
 * Fireblocks Sync admin page.
 *
 * Workflow:
 *   1. Banner shows which env this wallet-backend is bound to.
 *   2. "Load from Fireblocks" fetches /admin/fireblocks/supported-assets.
 *   3. Table lists every Fireblocks asset, flagged as mapped / unmapped / drift.
 *   4. "Map" opens a modal to associate an asset with a Coin + networkKey;
 *      POSTs /admin/fireblocks/sync-mapping which writes
 *      CoinNetworks.networks[i].fireblocksAssets[env].
 */
const FireblocksSync = () => {
  const [envInfo, setEnvInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [mapped, setMapped] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [statusFilter, setStatusFilter] = useState({
    label: "All",
    value: "all",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [coins, setCoins] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapTarget, setMapTarget] = useState(null);
  const [mapForm, setMapForm] = useState({ coinId: "", networkKey: "" });
  const [mapSubmitting, setMapSubmitting] = useState(false);
  const [page, setPage] = useState(1);

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "fireblocks-sync-list",
    allColumns: ALL_COLUMNS,
    defaultPageSize: 25,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const env = await getFireblocksEnv();
        if (env?.status) setEnvInfo(env.data);
        const coinsRes = await getCoin({ pagination: false });
        if (coinsRes?.status) {
          const list = Array.isArray(coinsRes?.data?.result)
            ? coinsRes.data.result
            : coinsRes?.data || [];
          setCoins(list);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load", { toastId: 'fireblocks-fetch-error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadFromFireblocks = async () => {
    setLoadingAssets(true);
    try {
      const res = await getFireblocksSupportedAssets();
      if (res?.status) {
        setMapped(res.data?.mapped || []);
        setUnmapped(res.data?.unmapped || []);
        toast.success(
          `Loaded ${(res.data?.mapped?.length || 0) + (res.data?.unmapped?.length || 0)} assets from Fireblocks`,
          { toastId: 'fireblocks-sync-success' }
        );
      } else {
        toast.error(res?.message || "Load failed", { toastId: 'fireblocks-sync-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", { toastId: 'fireblocks-sync-error' });
    } finally {
      setLoadingAssets(false);
    }
  };

  const filterValue = statusFilter?.value || "all";

  const rows = useMemo(() => {
    const combined = [
      ...mapped.map((r) => ({ ...r, _state: r.drift ? "drift" : "mapped" })),
      ...unmapped.map((r) => ({ ...r, _state: "unmapped" })),
    ];
    let filtered = combined;
    if (filterValue === "mapped")
      filtered = filtered.filter((r) => r._state === "mapped");
    if (filterValue === "unmapped")
      filtered = filtered.filter((r) => r._state === "unmapped");
    if (filterValue === "drift")
      filtered = filtered.filter((r) => r._state === "drift");
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.assetId || "").toLowerCase().includes(q) ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.nativeAsset || "").toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [mapped, unmapped, filterValue, searchTerm]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, filterValue]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const openMap = (row) => {
    setMapTarget(row);
    setMapForm({
      coinId: row.coinId || "",
      networkKey: row.networkKey || row.nativeAsset?.toLowerCase() || "",
    });
    setMapOpen(true);
  };

  const submitMap = async () => {
    if (!mapForm.coinId || !mapForm.networkKey) {
      toast.error("Select a coin and network key", { toastId: 'fireblocks-sync-error' });
      return;
    }
    setMapSubmitting(true);
    try {
      const res = await postFireblocksSyncMapping({
        coinId: mapForm.coinId,
        networkKey: mapForm.networkKey.toLowerCase(),
        env: envInfo?.env,
        assetId: mapTarget?.assetId,
      });
      if (res?.status) {
        toast.success("Mapping saved", { toastId: 'fireblocks-sync-success' });
        setMapOpen(false);
        loadFromFireblocks();
      } else {
        toast.error(res?.message || "Save failed", { toastId: 'fireblocks-sync-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: 'fireblocks-sync-error' });
    } finally {
      setMapSubmitting(false);
    }
  };

  const handleDownload = (format) => {
    if (!rows?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
    const cols = ALL_COLUMNS?.filter((c) => visibleColumnKeys?.includes(c?.key));
    triggerDownload(format, rows, cols, "fireblocks-sync");
  };

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, row) => (
      <div className="text-end">
        <Button size="small" type="primary" onClick={() => openMap(row)}>
          {row._state === "unmapped" ? "Map to coin" : "Re-sync"}
        </Button>
      </div>
    ),
  };

  const columns2 = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <div
            style={{
              background: envInfo ? ENV_COLOR[envInfo.env] || "#888" : "#888",
              color: "white",
              padding: "10px 16px",
              borderRadius: 8,
              marginBottom: 16,
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              Fireblocks env:{" "}
              <span style={{ textTransform: "uppercase" }}>
                {envInfo?.env || "…"}
              </span>
              {envInfo?.baseUrl ? (
                <span
                  style={{ opacity: 0.85, fontWeight: 400, marginLeft: 12 }}
                >
                  {envInfo.baseUrl}
                </span>
              ) : null}
            </span>
            <span style={{ fontSize: 12, opacity: 0.9 }}>
              workspace {envInfo?.workspace || "—"}
            </span>
          </div>

          {envInfo?.env === "production" && (
            <Alert
              type="warning"
              showIcon
              message="This admin session is connected to the PRODUCTION Fireblocks workspace. Every mapping change writes to the live coin config."
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <Button
              type="primary"
              loading={loadingAssets}
              onClick={loadFromFireblocks}
            >
              Load from Fireblocks
            </Button>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={rows.length}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        rowKey={(r) => r.assetId}
                        columns={columns2}
                        dataSource={pagedRows}
                        size="small"
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        locale={{
                          emptyText:
                            mapped.length + unmapped.length === 0
                              ? "Click “Load from Fireblocks” to fetch the supported-assets list."
                              : "No rows match the current filter.",
                        }}
                      />
                    )}
                    {rows.length > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={rows.length}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={`${
                          loading ? "display-hide" : "display-flex"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={rows.length}
                onClear={() => {
                  setSearchTerm("");
                  setStatusFilter({ label: "All", value: "all" });
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Asset ID, name or native"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
                <FilterPanel.Field label="Status">
                  <ReactSelect
                    options={statusOptions}
                    value={statusFilter || null}
                    onChange={(option) =>
                      setStatusFilter(option || { label: "All", value: "all" })
                    }
                    styles={customStyles}
                    placeholder="Any"
                    isClearable={false}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={
          mapTarget
            ? `Map ${mapTarget.assetId} (${mapTarget.nativeAsset})`
            : "Map asset"
        }
        open={mapOpen}
        onCancel={() => setMapOpen(false)}
        onOk={submitMap}
        confirmLoading={mapSubmitting}
        okText="Save mapping"
      >
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Coin</label>
            <Select
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              value={mapForm.coinId || undefined}
              onChange={(v) => setMapForm({ ...mapForm, coinId: v })}
              placeholder="Pick a coin"
              options={coins.map((c) => ({
                value: c._id,
                label: `${c.symbol || c.name} — ${c.name}`,
              }))}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              Network key (e.g. ethereum, tron, bsc, polygon)
            </label>
            <Input
              value={mapForm.networkKey}
              onChange={(e) =>
                setMapForm({
                  ...mapForm,
                  networkKey: e.target.value.toLowerCase(),
                })
              }
              placeholder="ethereum"
            />
          </div>
          <Alert
            type="info"
            showIcon
            message={`This writes fireblocksAssets.${envInfo?.env} for the selected coin + network. Min/max/fees are configured separately in the CoinNetwork form.`}
          />
        </div>
      </Modal>
    </>
  );
};

export default FireblocksSync;
